(() => {
  "use strict";

  function validDate(value) {
    if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    const parsed = new Date(value + "T12:00:00Z");
    return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
  }

  function localDate(now, timeZone = "America/Chicago") {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone, year: "numeric", month: "2-digit", day: "2-digit"
    }).formatToParts(now);
    const values = Object.fromEntries(parts.map(part => [part.type, part.value]));
    return values.year + "-" + values.month + "-" + values.day;
  }

  function displayState({ enabled, shippingDate, releaseDate, soldOut, nativeVisible, transitioning, invalidVariant, now = new Date(), timeZone = "America/Chicago" }) {
    if (!enabled) return "disabled";
    if (invalidVariant) return "invalid-variant";
    if (transitioning) return "transitioning";
    const date = shippingDate || releaseDate;
    if (!validDate(date) || !validDate(releaseDate)) return "unverified-date";
    if (localDate(now, timeZone) > releaseDate) return "expired";
    if (!soldOut) return "available";
    if (nativeVisible) return "native-date";
    return "visible";
  }

  function formattedDate(value, locale = "en") {
    if (!validDate(value)) return "";
    const date = new Date(value + "T12:00:00Z");
    if (locale.toLowerCase().startsWith("en")) {
      const day = date.getUTCDate();
      const teen = day % 100 >= 11 && day % 100 <= 13;
      const suffix = teen ? "th" : ({ 1: "st", 2: "nd", 3: "rd" }[day % 10] || "th");
      const month = new Intl.DateTimeFormat("en", { timeZone: "UTC", month: "long" }).format(date);
      return day + suffix + " " + month + " " + date.getUTCFullYear();
    }
    return new Intl.DateTimeFormat(locale, { timeZone: "UTC", day: "numeric", month: "long", year: "numeric" }).format(date);
  }

  function millisecondsToCheck(now, timeZone) {
    const parts = new Intl.DateTimeFormat("en", {
      timeZone, hourCycle: "h23", hour: "2-digit", minute: "2-digit", second: "2-digit"
    }).formatToParts(now);
    const values = Object.fromEntries(parts.map(part => [part.type, part.value]));
    const remaining = 86400 - Number(values.hour) * 3600 - Number(values.minute) * 60 - Number(values.second);
    return Math.min(60000, Math.max(50, remaining * 1000 - now.getMilliseconds() + 50));
  }

  const rules = { validDate, localDate, displayState, formattedDate, millisecondsToCheck };
  if (typeof module !== "undefined" && module.exports) {
    module.exports = rules;
    return;
  }

  if (customElements.get("pmv-preorder-date")) return;

  class PmvPreorderDate extends HTMLElement {
    connectedCallback() {
      try {
        this.config = JSON.parse(this.querySelector("[data-pmv-preorder-config]").textContent);
      } catch {
        this.dataset.pmvState = "invalid-config";
        this.hidden = true;
        return;
      }
      this.root = this.closest(".product__info-container");
      if (!this.root) return;
      this.variants = new Map(this.config.variants.map(variant => [String(variant.id), variant]));
      this.variantId = String(this.config.selectedVariantId);
      this.transitioning = false;
      this.changed = event => this.onChange(event);
      this.focused = () => this.schedule();
      this.root.addEventListener("change", this.changed, true);
      window.addEventListener("focus", this.focused);
      document.addEventListener("visibilitychange", this.focused);
      this.observer = new MutationObserver(mutations => {
        if (mutations.some(mutation => !this.contains(mutation.target))) this.schedule();
      });
      this.observer.observe(this.root, {
        subtree: true, childList: true, characterData: true, attributes: true,
        attributeFilter: ["hidden", "style", "class", "disabled", "aria-disabled", "aria-busy"]
      });
      if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, event => {
          if (String(event.data?.sectionId) !== this.dataset.sectionId) return;
          const variant = event.data?.variant;
          if (!variant) return;
          this.variantId = String(variant.id);
          this.variants.set(this.variantId, { ...this.variants.get(this.variantId), available: variant.available });
          this.schedule();
        });
      }
      this.schedule();
      this.setClock();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.root?.removeEventListener("change", this.changed, true);
      window.removeEventListener("focus", this.focused);
      document.removeEventListener("visibilitychange", this.focused);
      this.unsubscribe?.();
      cancelAnimationFrame(this.frame);
      clearTimeout(this.clock);
    }

    onChange(event) {
      if (event.target.closest("variant-selects, variant-radios")) {
        this.transitioning = true;
        this.setState("transitioning");
      }
      if (event.target.name === "id" && event.target.closest("form")?.id === this.dataset.formId) {
        this.variantId = String(event.target.value);
      }
      this.schedule();
    }

    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        if (this.isConnected) this.update();
      });
    }

    setClock() {
      clearTimeout(this.clock);
      this.clock = setTimeout(() => {
        this.schedule();
        if (this.isConnected) this.setClock();
      }, millisecondsToCheck(new Date(), this.dataset.timeZone));
    }

    visible(element) {
      if (!element || !element.textContent.trim() || !element.getClientRects().length) return false;
      for (let node = element; node && node !== this.root.parentElement; node = node.parentElement) {
        const style = getComputedStyle(node);
        if (node.hidden || style.display === "none" || style.visibility === "hidden" || style.visibility === "collapse" || Number(style.opacity) === 0) return false;
      }
      return true;
    }

    nativeVisible(id) {
      const wrapper = this.root.querySelector('[id="shipping_date_' + id + '"]');
      const date = wrapper?.querySelector(".shipping-info span:last-child");
      return this.visible(date);
    }

    normalizeNativeShippingDate(id, shippingDate) {
      // Correct only the two independently observed ID/date/text defects. A newer native
      // date or changed metadata must fail closed, including in an already-open page.
      const reviewed = {
        "52538416693468": { date: "2026-12-31", observed: "30th December 2026" },
        "52123302002908": { date: "2026-10-21", observed: "20th October 2026" }
      }[id];
      if (!reviewed || shippingDate !== reviewed.date ||
          !this.config.enabled || this.transitioning || !validDate(shippingDate)) return;
      const wrapper = this.root.querySelector('[id="shipping_date_' + id + '"].estimated-shipping-date-wrapper');
      const date = wrapper?.querySelector(':scope > .estimated-shipping-date[data-editable="estimated-shipping-date"] > .shipping-text > p.shipping-info > span:last-child');
      // Fail closed if Amai changes its markup, hides the message, or changes its format.
      if (!this.visible(date) || date.children.length || date.previousElementSibling?.tagName !== "SPAN" ||
          !/^\d{1,2}(st|nd|rd|th) [A-Za-z]+ \d{4}$/.test(date.textContent.trim())) return;
      const expected = formattedDate(shippingDate, "en");
      const observed = date.textContent.trim();
      if (observed === expected || observed !== reviewed.observed) return;
      date.textContent = expected;
    }


    update() {
      const form = document.getElementById(this.dataset.formId);
      const input = form?.querySelector('input[name="id"]');
      const id = String(input?.value || this.variantId);
      const variant = this.variants.get(id);
      const button = form?.querySelector('button[name="add"]');
      const label = button?.querySelector(".button__label")?.textContent || button?.querySelector(".button__content")?.textContent || button?.textContent || "";
      const normalized = value => value.replace(/\s+/g, " ").trim().toLowerCase();
      const soldOutText = normalized(label) === normalized(this.config.soldOutLabel);
      const unavailableText = normalized(label) === normalized(this.config.unavailableLabel);
      const loading = !!button?.classList.contains("loading") || button?.getAttribute("aria-busy") === "true";
      if (this.transitioning && variant && !loading && !unavailableText) {
        if (variant.available === false && soldOutText || variant.available === true && !soldOutText) this.transitioning = false;
      }
      const stable = id === this.variantId;
      const soldOut = !!variant && (variant.available === false || stable && !this.transitioning && button?.disabled && soldOutText);
      const shippingDate = variant?.shippingDate || this.config.shippingDate;
      const releaseDate = variant?.releaseDate || this.config.releaseDate;
      const state = displayState({
        enabled: this.config.enabled,
        shippingDate, releaseDate, soldOut,
        nativeVisible: this.nativeVisible(id),
        transitioning: this.transitioning || loading,
        invalidVariant: !variant || !button || unavailableText,
        timeZone: this.dataset.timeZone
      });
      if (variant && button && !unavailableText && !loading) {
        this.normalizeNativeShippingDate(id, shippingDate);
      }
      const date = shippingDate || releaseDate;
      if (state === "visible") {
        this.querySelector("[data-pmv-preorder-label]").textContent = shippingDate ? this.config.shippingLabel : this.config.releaseLabel;
        const time = this.querySelector("[data-pmv-preorder-date]");
        time.dateTime = date;
        time.textContent = formattedDate(date, document.documentElement.lang || "en");
      }
      this.dataset.pmvVariant = id;
      this.setState(state);
    }

    setState(state) {
      if (this.dataset.pmvState !== state) this.dataset.pmvState = state;
      const hidden = state !== "visible";
      if (this.hidden !== hidden) this.hidden = hidden;
    }
  }

  customElements.define("pmv-preorder-date", PmvPreorderDate);
})();
